import { useTranslation } from "@next-platform/client-common-core";
import { FilterV2, type FilterV2ActionHandler, type IFilterItem } from "@next-platform/client-common-spicy-ui";
import React from "react";

import Trans from "@/components/Trans/Trans";

import useFilterOptionAriaLabels from "./useFilterOptionAriaLabels";

type FilterSectionV2Props = {
  items: IFilterItem[];
  actionHandler: FilterV2ActionHandler;
  title: string;
  searchPlaceholder?: string;
  hasExclude?: boolean;
  defaultOpen?: boolean;
  hasActiveRefinements?: boolean;
  className?: string;
  bodyClassName?: string;
  "data-testid"?: string;
};

const FilterSectionV2 = ({
  items,
  actionHandler,
  title,
  searchPlaceholder,
  hasExclude = false,
  defaultOpen = true,
  hasActiveRefinements,
  className,
  bodyClassName,
  "data-testid": testId = "FilterSectionV2",
}: FilterSectionV2Props) => {
  const { t } = useTranslation();
  const { getIncludeAriaLabel, getExcludeAriaLabel } = useFilterOptionAriaLabels();
  const emptySearchText = searchPlaceholder ? t("chatbotFilters:filterSearch.noResults") : undefined;

  return (
    <FilterV2
      actionHandler={actionHandler}
      defaultOpen={defaultOpen}
      getExcludeAriaLabel={getExcludeAriaLabel}
      getIncludeAriaLabel={getIncludeAriaLabel}
      items={items}
      className={className}
      data-testid={testId}
    >
      <FilterV2.Header>
        <FilterV2.Title>{title}</FilterV2.Title>
        <FilterV2.Count />
        <FilterV2.Reset alwaysShow={hasActiveRefinements} data-testid={`${testId}-Reset`}>
          <Trans i18nKey="common:reset" />
        </FilterV2.Reset>
      </FilterV2.Header>
      <FilterV2.Body className={bodyClassName} emptySearchText={emptySearchText} hasExclude={hasExclude}>
        {searchPlaceholder ? <FilterV2.Search aria-label={searchPlaceholder} placeholder={searchPlaceholder} /> : null}
      </FilterV2.Body>
    </FilterV2>
  );
};

export default React.memo(FilterSectionV2);
